import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js"
import { Line } from "react-chartjs-2"
import { colors } from "../../lib/colors"
import type { ChartData } from "../../types/analytics"
import { pickGranularity, buildAxisTickLabels, formatTooltipDate } from "../../lib/chartAxis"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

interface LineChartProps {
  data: ChartData
  color?: string
  beginAtZero?: boolean
  /** labels が ISO 日付（YYYY-MM-DD）のとき true。期間に応じて軸を日/月/年へ自動整形する */
  dateAxis?: boolean
}

const LineChart = ({ data, color = colors.primary, beginAtZero = false, dateAxis = false }: LineChartProps) => {
  const isMultiSeries = data.datasets.length > 1

  const granularity = dateAxis ? pickGranularity(data.labels) : null
  const tickLabels  = granularity ? buildAxisTickLabels(data.labels, granularity) : null

  const chartData = {
    labels: data.labels,
    datasets: data.datasets.map(ds => ({
      ...ds,
      borderColor:      ds.borderColor     ?? color,
      backgroundColor:  ds.backgroundColor ?? `${color}20`,
      fill:             ds.fill            ?? !isMultiSeries,
      tension:          0.4,
      pointRadius:      3,
      pointHoverRadius: 5,
      spanGaps:         true,
    })),
  }

  return (
    <Line
      data={chartData}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: isMultiSeries,
            position: "top" as const,
            labels: { font: { size: 11 }, padding: 12, boxWidth: 12 },
          },
          ...(dateAxis && {
            tooltip: {
              callbacks: {
                title: (items: { dataIndex: number }[]) =>
                  items.length ? formatTooltipDate(data.labels[items[0].dataIndex]) : "",
              },
            },
          }),
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: tickLabels
              ? {
                  font: { size: 11 },
                  autoSkip: granularity === "day",
                  maxTicksLimit: granularity === "day" ? 8 : undefined,
                  // category scale の第1引数はデータindex。autoSkipで間引かれてもズレないよう value を使う
                  callback: (value: string | number) => tickLabels[Number(value)] ?? "",
                }
              : { maxTicksLimit: 8, font: { size: 11 } },
          },
          y: { grid: { color: "#f3f4f6" }, ticks: { font: { size: 11 } }, beginAtZero },
        },
      }}
    />
  )
}

export default LineChart
